import { useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function TaskChecklistEditor({ tasks = [], onChange }) {
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newRequired, setNewRequired] = useState(true);

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const newTask = {
      id: `temp-${Date.now()}`,
      title: newTitle.trim(),
      description: newDescription.trim(),
      is_required: newRequired,
      order: tasks.length,
    };
    onChange([...tasks, newTask]);
    setNewTitle("");
    setNewDescription("");
    setNewRequired(true);
  };

  const handleRemoveTask = (index) => {
    const updated = tasks.filter((_, i) => i !== index).map((t, idx) => ({ ...t, order: idx }));
    onChange(updated);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...tasks];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    onChange(updated.map((t, idx) => ({ ...t, order: idx })));
  };

  const handleMoveDown = (index) => {
    if (index === tasks.length - 1) return;
    const updated = [...tasks];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    onChange(updated.map((t, idx) => ({ ...t, order: idx })));
  };

  return (
    <div className="flex flex-col gap-5">
      {tasks.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {tasks.map((task, index) => (
            <li
              key={task.id || index}
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 shadow-xs"
            >
              <div className="flex flex-col gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  title="Move up"
                  aria-label="Move task up"
                >
                  <ChevronUp className="size-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === tasks.length - 1}
                  title="Move down"
                  aria-label="Move task down"
                >
                  <ChevronDown className="size-3.5" />
                </Button>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <span>
                    {index + 1}. {task.title}
                  </span>
                  {task.is_required ? (
                    <Badge variant="destructive" className="text-[0.65rem] uppercase">
                      Required
                    </Badge>
                  ) : null}
                </div>
                {task.description ? (
                  <p className="mt-0.5 text-sm text-muted-foreground">{task.description}</p>
                ) : null}
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-destructive hover:text-destructive"
                onClick={() => handleRemoveTask(index)}
                title="Remove task"
                aria-label="Remove task"
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No tasks added to this template yet.
        </div>
      )}

      <div
        className={
          tasks.length > 0
            ? "mt-1 space-y-3 border-t border-dashed border-border pt-5"
            : "space-y-3"
        }
      >
        <div className="text-sm font-semibold">Add New Task</div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            type="text"
            className="sm:flex-1"
            placeholder="Task Title *"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddTask(e)}
          />
          <Input
            type="text"
            className="sm:flex-[2]"
            placeholder="Instructions / Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddTask(e)}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="new-task-required"
              checked={newRequired}
              onCheckedChange={(checked) => setNewRequired(checked === true)}
            />
            <Label htmlFor="new-task-required" className="cursor-pointer font-normal">
              Make this a required task
            </Label>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleAddTask}
            disabled={!newTitle.trim()}
          >
            Add Task
          </Button>
        </div>
      </div>
    </div>
  );
}
